import type {
  SalesApiDatasource,
  ListTransactionsInput,
  ListContractsInput,
  ListOffersInput,
  OffersListResponse,
  ListProviderAccountsInput,
  ListSaleWebhookInboxInput,
} from '../datasources/sales-api.datasource';
import type {
  TransactionsListResponse,
  TransactionDetail,
  TransactionInvoices,
  InvoiceTransaction,
  TransactionContract,
  BillingAgreement,
  TransactionOfferAndArrangements,
  Offer,
  TransactionEventsResponse,
  SalesCorrelatedEntityEventsResponse,
  SalesAuditEntityType,
  CancelBillingAgreementInput,
  CancelBillingAgreementResult,
  ResolveCycleCountInput,
  ResolveCycleCountResult,
  SyncChargeHistoryInput,
  SyncChargeHistoryResult,
  SyncContractChargeHistoryResult,
  CreateDebtArrangementInput,
  ActivateDebtArrangementInput,
  UpdateDebtArrangementDraftInput,
  DebtArrangement,
  DebtArrangementWithRelations,
  DebtArrangementReasonInput,
  AbandonContractInput,
  AbandonContractResult,
  ContractsListResponse,
  ContractDetail,
  ContractInvoices,
  ContractBillingAgreements,
  BillingAgreementDetail,
  ProviderAccount,
  SaleWebhookInboxListResponse,
  SaleWebhookInbox,
  ForceProcessSaleWebhookInboxInput,
  JustifiedActionInput,
  ManuallySettleInvoiceInput,
  ManuallySettleInvoiceResult,
  InvalidateManualTransactionResult,
  UploadAttachmentResult,
  CloseOperationalAlertInput,
  ListSalesOperationalAlertsInput,
  OperationalAlertFacet,
  SalesOperationalAlertStatus,
  SalesOperationalAlert,
  SalesOperationalAlertsListResponse,
} from '../../domain/entities/sales.entity';

export class SalesRepositoryImpl {
  private api: SalesApiDatasource;

  constructor(api: SalesApiDatasource) {
    this.api = api;
  }

  async listTransactions(
    input?: ListTransactionsInput,
  ): Promise<TransactionsListResponse> {
    return this.api.listTransactions(input);
  }

  async getTransaction(id: string): Promise<TransactionDetail> {
    return this.api.getTransaction(id);
  }

  async getTransactionInvoices(id: string): Promise<TransactionInvoices> {
    return this.api.getTransactionInvoices(id);
  }

  async getInvoiceTransactions(id: string): Promise<InvoiceTransaction[]> {
    return this.api.getInvoiceTransactions(id);
  }

  async getTransactionContract(id: string): Promise<TransactionContract> {
    return this.api.getTransactionContract(id);
  }

  async getTransactionBillingAgreement(id: string): Promise<BillingAgreement> {
    return this.api.getTransactionBillingAgreement(id);
  }

  async getTransactionOfferAndArrangements(
    id: string,
  ): Promise<TransactionOfferAndArrangements> {
    return this.api.getTransactionOfferAndArrangements(id);
  }

  async getTransactionEvents(
    id: string,
    params: { page: number; perPage: number },
  ): Promise<TransactionEventsResponse> {
    return this.api.getTransactionEvents(id, params);
  }

  async getEntityEvents(
    entityType: SalesAuditEntityType,
    entityId: string,
    params: { page: number; perPage: number },
  ): Promise<TransactionEventsResponse> {
    return this.api.getEntityEvents(entityType, entityId, params);
  }

  async getEntityEventsByCorrelationId(
    correlationId: string,
    params: { page: number; perPage: number },
  ): Promise<SalesCorrelatedEntityEventsResponse> {
    return this.api.getEntityEventsByCorrelationId(correlationId, params);
  }

  async cancelBillingAgreement(
    billingAgreementId: string,
    body: CancelBillingAgreementInput,
  ): Promise<CancelBillingAgreementResult> {
    return this.api.cancelBillingAgreement(billingAgreementId, body);
  }

  async resolveBillingAgreementCycleCount(
    billingAgreementId: string,
    body: ResolveCycleCountInput,
  ): Promise<ResolveCycleCountResult> {
    return this.api.resolveBillingAgreementCycleCount(billingAgreementId, body);
  }

  async syncBillingAgreementChargeHistory(
    billingAgreementId: string,
    body: SyncChargeHistoryInput,
  ): Promise<SyncChargeHistoryResult> {
    return this.api.syncBillingAgreementChargeHistory(billingAgreementId, body);
  }

  async syncContractChargeHistory(
    contractId: string,
    body: SyncChargeHistoryInput,
  ): Promise<SyncContractChargeHistoryResult> {
    return this.api.syncContractChargeHistory(contractId, body);
  }

  async createDebtArrangement(
    body: CreateDebtArrangementInput,
  ): Promise<DebtArrangement> {
    return this.api.createDebtArrangement(body);
  }

  async activateDebtArrangement(
    debtArrangementId: string,
    body: ActivateDebtArrangementInput,
  ): Promise<DebtArrangement> {
    return this.api.activateDebtArrangement(debtArrangementId, body);
  }

  async updateDebtArrangementDraft(
    debtArrangementId: string,
    body: UpdateDebtArrangementDraftInput,
  ): Promise<DebtArrangement> {
    return this.api.updateDebtArrangementDraft(debtArrangementId, body);
  }

  async cancelDebtArrangement(
    debtArrangementId: string,
    body: DebtArrangementReasonInput,
  ): Promise<DebtArrangement> {
    return this.api.cancelDebtArrangement(debtArrangementId, body);
  }

  async revertDebtArrangement(
    debtArrangementId: string,
    body: DebtArrangementReasonInput,
  ): Promise<unknown> {
    return this.api.revertDebtArrangement(debtArrangementId, body);
  }

  async abandonContract(
    contractId: string,
    body: AbandonContractInput,
  ): Promise<AbandonContractResult> {
    return this.api.abandonContract(contractId, body);
  }

  async listContracts(
    input?: ListContractsInput,
  ): Promise<ContractsListResponse> {
    return this.api.listContracts(input);
  }

  async getContract(id: string): Promise<ContractDetail> {
    return this.api.getContract(id);
  }

  async getContractInvoices(id: string): Promise<ContractInvoices> {
    return this.api.getContractInvoices(id);
  }

  async getContractOfferAndArrangements(
    id: string,
  ): Promise<TransactionOfferAndArrangements> {
    return this.api.getContractOfferAndArrangements(id);
  }

  async getContractBillingAgreements(
    id: string,
  ): Promise<ContractBillingAgreements> {
    return this.api.getContractBillingAgreements(id);
  }

  async getBillingAgreement(id: string): Promise<BillingAgreementDetail> {
    return this.api.getBillingAgreement(id);
  }

  async getDebtArrangement(id: string): Promise<DebtArrangementWithRelations> {
    return this.api.getDebtArrangement(id);
  }

  async listOffers(input?: ListOffersInput): Promise<OffersListResponse> {
    return this.api.listOffers(input);
  }

  async getOffer(id: string): Promise<Offer> {
    return this.api.getOffer(id);
  }

  async listProviderAccounts(
    input?: ListProviderAccountsInput,
  ): Promise<ProviderAccount[]> {
    return this.api.listProviderAccounts(input);
  }

  async listWebhookInbox(
    input?: ListSaleWebhookInboxInput,
  ): Promise<SaleWebhookInboxListResponse> {
    return this.api.listWebhookInbox(input);
  }

  async getWebhookInboxRow(id: string): Promise<SaleWebhookInbox> {
    return this.api.getWebhookInboxRow(id);
  }

  async forceProcessWebhookInbox(
    id: string,
    body: ForceProcessSaleWebhookInboxInput,
  ): Promise<SaleWebhookInbox> {
    return this.api.forceProcessWebhookInbox(id, body);
  }

  async uploadAttachment(file: File): Promise<UploadAttachmentResult> {
    return this.api.uploadAttachment(file);
  }

  async manuallySettleInvoice(
    invoiceId: string,
    body: ManuallySettleInvoiceInput,
  ): Promise<ManuallySettleInvoiceResult> {
    return this.api.manuallySettleInvoice(invoiceId, body);
  }

  listOperationalAlertFacets(
    status?: SalesOperationalAlertStatus,
  ): Promise<OperationalAlertFacet[]> {
    return this.api.listOperationalAlertFacets(status);
  }

  async listOperationalAlerts(
    input?: ListSalesOperationalAlertsInput,
  ): Promise<SalesOperationalAlertsListResponse> {
    return this.api.listOperationalAlerts(input);
  }

  async resolveOperationalAlert(
    alertId: string,
    body: CloseOperationalAlertInput,
  ): Promise<SalesOperationalAlert> {
    return this.api.resolveOperationalAlert(alertId, body);
  }

  async dismissOperationalAlert(
    alertId: string,
    body: CloseOperationalAlertInput,
  ): Promise<SalesOperationalAlert> {
    return this.api.dismissOperationalAlert(alertId, body);
  }

  async reopenOperationalAlertForEntity(
    alertId: string,
    body: { note: string },
  ): Promise<SalesOperationalAlert> {
    return this.api.reopenOperationalAlertForEntity(alertId, body);
  }

  async invalidateManualTransaction(
    transactionId: string,
    body: JustifiedActionInput,
  ): Promise<InvalidateManualTransactionResult> {
    return this.api.invalidateManualTransaction(transactionId, body);
  }
}
